"use client";

import { useEffect, useState } from "react";

/**
 * EPIC-048 STORY-2001 — connectivity indicator banner.
 *
 * Shows a thin top banner when `navigator.onLine` is false. Listens
 * for `online` / `offline` window events to flip in real time. Self-
 * hidden when the page is online (no DOM, no layout cost when not
 * needed).
 *
 * Pairs with the SW's offline-shell fallback: navigation requests
 * that fail return /offline.html (full-page offline experience);
 * this banner handles the in-app case where the user is on a
 * cached/already-loaded page when connectivity drops mid-session.
 */
export function OfflineIndicator() {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOnline(navigator.onLine);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      className="w-full border-b text-center text-[11px] font-bold uppercase tracking-[0.5px]"
      style={{
        borderColor:
          "color-mix(in srgb, var(--color-no) 40%, transparent)",
        background: "color-mix(in srgb, var(--color-no) 14%, transparent)",
        color: "var(--color-no)",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-5 py-1.5">
        Offline · changes won&apos;t save until you reconnect
      </div>
    </div>
  );
}
